import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        setAccessDenied(null);
      } else if (res.status === 401 || res.status === 403) {
        // Genuine auth failure: clear session and surface any ban message.
        setUser(null);
        const data = await res.json().catch(() => ({}));
        setAccessDenied(res.status === 403 ? data.detail : null);
      }
      // Other statuses (5xx, etc.): keep last known state, don't log the user out.
    } catch {
      // Network error (e.g. mobile returning from background): keep last known
      // auth state so the user stays on their current page instead of the home gate.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const userId = user?.user_id;
  useEffect(() => {
    const onFocus = () => { if (!window.location.hash?.includes('session_id=')) checkAuth(); };
    window.addEventListener('focus', onFocus);
    const timer = userId ? setInterval(checkAuth, 60000) : null;
    return () => { window.removeEventListener('focus', onFocus); if (timer) clearInterval(timer); };
  }, [userId, checkAuth]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch { /* noop */ }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, setUser, checkAuth, logout, setAccessDenied }}>
      {accessDenied ? <main data-testid="access-suspended" className="min-h-screen bg-[#0B0C0E] flex items-center justify-center p-6">
        <div className="max-w-lg border border-red-500/25 rounded-xl bg-[#121418] p-8">
          <p className="text-amber-500 text-xs font-mono tracking-widest mb-4">STAGE CREW</p>
          <h1 data-testid="access-suspended-title" className="text-3xl font-bold text-white mb-4">Acceso suspendido</h1>
          <p data-testid="access-suspended-message" role="alert" className="text-sm text-slate-300 leading-relaxed">{accessDenied}</p>
          <p className="text-xs text-slate-500 mt-4" data-testid="access-suspended-network-note">Un veto por IP también puede afectar a personas que comparten la misma conexión.</p>
          <button data-testid="access-suspended-recheck" onClick={checkAuth} className="mt-6 px-4 py-2 rounded-lg border border-[#2A2F3D] hover:border-amber-500 text-sm text-slate-200 transition-colors sc-focus">Comprobar acceso</button>
        </div>
      </main> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + '/';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};
